import { Button } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { map } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { automationSourcesApi } from 'api';
import {
  FormActions,
  FormControls,
  FormikCheckbox,
  FormikSelect,
  FormikTextField,
} from 'components';
import { AutomationSourceType } from 'enums';
import { useMutation, useUserContextUtils } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { AutomationSource, CreateAutomationSourceDto } from 'types';
import { validationUtils } from 'utils';

import { useAutomationSources } from './AutomationSourcesList.context';

type Values = CreateAutomationSourceDto;

type Props = {
  onSubmit?: (values: AutomationSource) => void;
  selectedAutomationSource?: AutomationSource | null;
  withType?: Boolean;
};

export const AutomationSourcesDetailsForm: React.FC<Props> = ({
  onSubmit,
  selectedAutomationSource = null,
  withType = false,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.automation_sources',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { tradeMethod, automationSourcesQuery } = useAutomationSources();
  const { getTradeMethodsOptions, allTradeMethodsOptions } =
    useUserContextUtils();

  const tradeMethodsOptions = useMemo(
    () =>
      selectedAutomationSource?.id
        ? allTradeMethodsOptions()
        : getTradeMethodsOptions(),
    [selectedAutomationSource, getTradeMethodsOptions, allTradeMethodsOptions],
  );

  const initialValues: Values = useMemo(
    () => ({
      name: selectedAutomationSource?.name ?? '',
      tradeMethodId:
        tradeMethod?.id || selectedAutomationSource?.tradeMethodId || '',
      type: selectedAutomationSource?.type ?? AutomationSourceType.Payin,
      allowMultipleTradeMethods:
        selectedAutomationSource?.allowMultipleTradeMethods ?? false,
      namePattern: selectedAutomationSource?.namePattern ?? '',
      isRegexEnabled: selectedAutomationSource?.isRegexEnabled ?? false,
    }),
    [selectedAutomationSource, tradeMethod],
  );

  const typeOptions = useMemo(
    () =>
      map(AutomationSourceType, (type) => ({
        value: type,
        label: t(`types.${type}`),
      })),
    [t],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(tCommon('errors.required')),
        namePattern: Yup.string().when('isRegexEnabled', {
          is: true,
          then: (schema) =>
            schema
              .required(tCommon('errors.required'))
              .test(
                'valid-regex',
                tCommon('errors.invalid'),
                validationUtils.isValidRegexPattern,
              ),
          otherwise: (schema) => schema.optional().nullable(),
        }),
        isRegexEnabled: Yup.boolean(),
      }),
    [tCommon],
  );

  const createAutomationSource = useMutation<
    AutomationSource,
    AxiosError,
    Values
  >((automationSource) =>
    automationSourcesApi.create({
      ...automationSource,
      tradeMethodId:
        automationSource.type === AutomationSourceType.Payin
          ? automationSource.tradeMethodId
          : null,
    }),
  );

  const updateAutomationSource = useMutation<
    AutomationSource,
    AxiosError,
    Values
  >((automationSource) =>
    automationSourcesApi.update({
      id: selectedAutomationSource!.id,
      data: {
        ...automationSource,
        tradeMethodId:
          automationSource.type === AutomationSourceType.Payin
            ? automationSource.tradeMethodId
            : null,
      },
    }),
  );

  const doSubmit = useMemo(() => {
    if (selectedAutomationSource) {
      return updateAutomationSource;
    }
    return createAutomationSource;
  }, [
    selectedAutomationSource,
    createAutomationSource,
    updateAutomationSource,
  ]);

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      doSubmit.mutate(values, {
        onSuccess: (values) => {
          automationSourcesQuery.refetch();
          onSubmit?.(values);
        },
        onError: (error: AxiosError) => {
          formikHelpers.setErrors(validationUtils.getFormErrors(error));
        },
        onSettled: () => {
          formikHelpers.setSubmitting(false);
        },
      });
    },
    [onSubmit, automationSourcesQuery, doSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values }) => (
        <Form>
          <FormControls>
            <FormikTextField
              label={t('details_form.fields.name')}
              name="name"
              required
            />
            {withType && (
              <FormikSelect
                label={t('details_form.fields.type')}
                name="type"
                options={typeOptions}
                required
              />
            )}
            {withType && values.type === AutomationSourceType.Payin && (
              <FormikSelect
                label={t('details_form.fields.trade_method')}
                name="tradeMethodId"
                options={tradeMethodsOptions}
                required
              />
            )}
            {withType && values.type === AutomationSourceType.Payin && (
              <FormikCheckbox
                name="allowMultipleTradeMethods"
                label={t('details_form.fields.allow_multiple_trade_methods')}
              />
            )}
            <FormikCheckbox
              name="isRegexEnabled"
              label={t('details_form.fields.is_regex_enabled')}
            />
            {values.isRegexEnabled && (
              <FormikTextField
                label={t('details_form.fields.name_pattern')}
                name="namePattern"
                helperText={t('details_form.fields.name_pattern_hint')}
                required
              />
            )}
          </FormControls>
          <FormActions>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={isSubmitting}
            >
              {tCommon('buttons.save')}
            </Button>
          </FormActions>
        </Form>
      )}
    </Formik>
  );
};
